import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';

/** Who an admin email goes to. The worker resolves this to addresses in the database. */
export type Audience =
  | { kind: 'person' | 'household' | 'dojo'; id: string }
  | { kind: 'all_current' }
  | { kind: 'people'; person_ids: string[]; label?: string };   // label: what the list is, e.g. "Booked on Winter Gasshuku 2026"

export interface AudienceRow { person_id: string; household_id: string; first_name: string | null; to_email: string; dojo: string | null; member_until: string | null; member_number: number | null }
export interface EmailTemplate { id: string; name: string; subject: string; body_text: string; updated_at: string; for_instructors: boolean }
export interface EmailBatch { id: string; subject: string; body_text: string; audience: Audience; audience_label: string; recipient_count: number; sent_count: number; failed_count: number; sent_by_name: string; created_at: string; completed_at: string | null; sent_by: string; sent_as: 'admin' | 'instructor'; reply_to: string | null }

/** The compose link for an audience; a list of people travels in router state, as it can be long. */
export function composeLink(a: Audience, base = '/admin/email/new'): { to: string; state?: { audience: Audience } } {
  return a.kind === 'people' ? { to: base, state: { audience: a } }
    : { to: `${base}?to=${a.kind === 'all_current' ? 'all_current' : `${a.kind}:${a.id}`}` };
}

/** An instructor's compose screen, for their dojo or one student. */
export const dojoComposeLink = (a: { kind: 'dojo' | 'person'; id: string }) => composeLink(a, '/dojo/email');

export const BATCH_COLUMNS = 'id,subject,body_text,audience,audience_label,recipient_count,sent_count,failed_count,sent_by_name,created_at,completed_at,sent_by,sent_as,reply_to';

export function parseTo(to: string | null): Audience | null {
  if (!to) return null;
  if (to === 'all_current') return { kind: 'all_current' };
  const [kind, id] = to.split(':');
  return (kind === 'person' || kind === 'household' || kind === 'dojo') && id ? { kind, id } : null;
}

export const useEmailAudience = (a: Audience | null, fn: 'admin_email_audience' | 'instructor_email_audience' = 'admin_email_audience') => useQuery({
  queryKey: ['email-audience', fn, a],
  enabled: !!a,
  queryFn: async () => {
    const { data, error } = await supabase.rpc(fn, { p_audience: a });
    if (error) throw error;
    return (data ?? []) as AudienceRow[];
  },
});

export const useEmailTemplates = () => useQuery({
  queryKey: ['email-templates'],
  queryFn: async () => {
    const { data, error } = await supabase.from('email_template').select('id,name,subject,body_text,updated_at,for_instructors').order('name');
    if (error) throw error;
    return data as EmailTemplate[];
  },
});

/** Sent emails. `mine` (a user id) keeps to one sender's own, for My dojo; admins otherwise see everyone's. */
export const useEmailBatches = ({ mine }: { mine?: string } = {}) => useQuery({
  queryKey: ['email-batches', mine ?? 'all'],
  queryFn: async () => {
    let q = supabase.from('email_batch').select(BATCH_COLUMNS).order('created_at', { ascending: false }).limit(200);
    if (mine) q = q.eq('sent_by', mine);
    const { data, error } = await q;
    if (error) throw error;
    return data as EmailBatch[];
  },
});
